import { useRef, useState } from 'react';
import moment from 'moment';
import {
  DotsThree,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  PushPin,
  PushPinSlash,
  Trash,
  X,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { ChatConversationsController } from './useChatConversations';
import { useChatHistorySearch } from './useChatHistorySearch';
import { useScrollEdgeFade } from './useScrollEdgeFade';

export interface ChatConversationListProps {
  conversations: ChatConversationsController;
  /**
   * Called after the student picks a conversation or starts a new one — the
   * drawer's popover and the page's narrow history view close on it.
   */
  onAfterSelect?: () => void;
  /**
   * Whether the list leads with its own New Chat button. The page's header
   * carries one beside the history toggle, so its sidebar turns this off; the
   * mobile sheet, whose header has no such button, keeps it.
   */
  showNewChat?: boolean;
  /**
   * A row's actions menu opened or closed. The menu renders in a portal and
   * takes the page's pointer and focus while open, so a surface that styles
   * itself on hover or focus (the desktop's history rail) needs telling.
   */
  onMenuOpenChange?: (isOpen: boolean) => void;
  className?: string;
}

/**
 * The student's chat history: start a new conversation, switch to an old one,
 * delete one (ORA-458). Parity with the legacy panel's sidebar, minus its
 * content-type filter — the companion has one thread per conversation and the
 * filter existed to separate per-content threads the old drawer created.
 *
 * It is a list, not a surface: the page renders it in a sidebar and the drawer
 * in a popover, and both feed it the same controller so they never disagree
 * about what history is.
 */
const ChatConversationList = ({
  conversations,
  onAfterSelect,
  showNewChat = true,
  onMenuOpenChange,
  className,
}: ChatConversationListProps) => {
  /**
   * How the open row menu is being driven. Closing hands focus back to its
   * trigger, and Chrome counts that as keyboard focus — which held the
   * desktop rail solid (and ringed the trigger) after a mouse user had
   * clicked away. Focus goes back only when the menu was used from the
   * keyboard.
   */
  const isMenuPointerDriven = useRef(false);
  /**
   * Which row is asking to be confirmed. Deleting a conversation removes it and
   * its messages on the server outright — there is no undo and no trash — so
   * the row asks once rather than acting on a mis-tap next to "open".
   */
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const [renameConversationId, setRenameConversationId] = useState<
    string | null
  >(null);

  const [renameTitle, setRenameTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const {
    conversations: items,
    activeConversationId,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    conversationError,
    select,
    startNew,
    remove,
    rename,
    setPinned,
    loadMore,
  } = conversations;

  const search = useChatHistorySearch(searchQuery, items);

  // The list fades out at its bottom edge while there is more to scroll to
  // (ORA-628), instead of the last row being cut off hard by the frame.
  const {
    ref: listRef,
    hasMoreAbove,
    hasMoreBelow,
  } = useScrollEdgeFade<HTMLUListElement>();

  const isSearching = Boolean(searchQuery.trim());
  const visibleItems = isSearching ? search.items : items;
  const visibleIsLoading = isSearching ? search.isLoading : isLoading;

  const visibleIsLoadingMore = isSearching
    ? search.isLoadingMore
    : isLoadingMore;

  const visibleHasMore = isSearching ? search.hasMore : hasMore;
  const visibleError = isSearching ? search.error || conversationError : error;

  const renameConversation = visibleItems.find(
    (conversation) => conversation.id === renameConversationId
  );

  const pinnedConversations = visibleItems.filter(
    (conversation) => conversation.pinnedAt
  );

  const recentConversations = visibleItems.filter(
    (conversation) => !conversation.pinnedAt
  );

  const openRename = (conversationId: string, title: string) => {
    setRenameConversationId(conversationId);
    setRenameTitle(title);
  };

  const submitRename = () => {
    if (!renameConversationId || !renameTitle.trim()) return;
    void rename(renameConversationId, renameTitle.trim());
    setRenameConversationId(null);
  };

  const renderConversation = (conversation: (typeof items)[number]) => {
    const title = conversation.title || 'Untitled chat';
    const isActive = conversation.id === activeConversationId;

    return (
      <li key={conversation.id} className="group flex items-center gap-1">
        <button
          type="button"
          aria-current={isActive ? 'true' : undefined}
          aria-label={`Open ${title}`}
          onClick={() => {
            void select(conversation.id);
            onAfterSelect?.();
          }}
          className={cn(
            'min-w-0 flex-1 cursor-pointer rounded-md border-0 px-2 py-1.5 text-left transition-colors hover:bg-surface-highlight',
            isActive ? 'bg-surface-highlight' : 'bg-transparent'
          )}
        >
          <span
            title={title}
            className="line-clamp-2 break-words text-[13px] leading-4 font-medium text-fg-primary"
          >
            {title}
          </span>
          <span className="block truncate font-sans text-xs text-fg-secondary">
            {conversation.legacy ? 'Archived · ' : ''}
            {conversation.lastMessageAt
              ? moment(conversation.lastMessageAt).format('MMM D, YYYY')
              : ''}
          </span>
        </button>
        {pendingDeleteId === conversation.id ? (
          <>
            <Button
              size="sm"
              variant="destructive"
              aria-label={`Confirm deleting ${title}`}
              onClick={() => {
                setPendingDeleteId(null);
                void remove(conversation.id);
              }}
            >
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Keep ${title}`}
              onClick={() => setPendingDeleteId(null)}
            >
              Keep
            </Button>
          </>
        ) : (
          <DropdownMenu onOpenChange={onMenuOpenChange}>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`Actions for ${title}`}
                onPointerDown={() => {
                  isMenuPointerDriven.current = true;
                }}
                onKeyDown={() => {
                  isMenuPointerDriven.current = false;
                }}
                className="opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
              >
                <DotsThree size={16} weight="bold" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onPointerDown={() => {
                isMenuPointerDriven.current = true;
              }}
              onKeyDown={() => {
                isMenuPointerDriven.current = false;
              }}
              onCloseAutoFocus={(event) => {
                if (isMenuPointerDriven.current) event.preventDefault();
              }}
            >
              <DropdownMenuItem
                onSelect={() => openRename(conversation.id, title)}
              >
                <PencilSimple size={15} aria-hidden="true" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  void setPinned(conversation.id, !conversation.pinnedAt)
                }
              >
                {conversation.pinnedAt ? (
                  <PushPinSlash size={15} aria-hidden="true" />
                ) : (
                  <PushPin size={15} aria-hidden="true" />
                )}
                {conversation.pinnedAt ? 'Unpin' : 'Pin'}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => setPendingDeleteId(conversation.id)}
              >
                <Trash size={15} aria-hidden="true" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </li>
    );
  };

  return (
    <div
      className={cn('flex min-h-0 flex-col gap-2', className)}
      data-testid="chat-conversation-list"
    >
      {showNewChat ? (
        <Button
          size="sm"
          variant="outline"
          className="justify-start"
          onClick={() => {
            startNew();
            onAfterSelect?.();
          }}
        >
          <Plus size={14} aria-hidden="true" />
          New Chat
        </Button>
      ) : null}

      <div className="relative shrink-0">
        <MagnifyingGlass
          size={15}
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-secondary"
        />
        <Input
          type="search"
          inputSize="sm"
          aria-label="Search chats"
          placeholder="Search chats…"
          maxLength={80}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && searchQuery) {
              event.preventDefault();
              event.stopPropagation();
              setSearchQuery('');
            }
          }}
          className="pr-9 pl-9 [&::-webkit-search-cancel-button]:appearance-none"
        />
        {searchQuery ? (
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Clear chat search"
            className="absolute top-1/2 right-0.5 -translate-y-1/2"
            onClick={() => setSearchQuery('')}
          >
            <X size={14} aria-hidden="true" />
          </Button>
        ) : null}
      </div>

      {visibleError ? (
        <p role="status" className="m-0 px-1 text-xs text-fg-secondary">
          {visibleError}
        </p>
      ) : null}

      {isSearching && search.error && !search.hasMore ? (
        <Button size="sm" variant="link" onClick={search.retry}>
          Retry Search
        </Button>
      ) : null}

      {visibleIsLoading && visibleItems.length === 0 ? (
        <p role="status" className="m-0 px-1 text-xs text-fg-secondary">
          {isSearching ? 'Searching chats…' : 'Loading chats…'}
        </p>
      ) : null}

      {!visibleIsLoading && visibleItems.length === 0 && !visibleError ? (
        <p role="status" className="m-0 px-1 text-xs text-fg-secondary">
          {isSearching ? 'No matching chats.' : 'No chats yet.'}
        </p>
      ) : null}

      <ul
        ref={listRef}
        data-testid="chat-conversation-rows"
        className={cn(
          'm-0 flex min-h-0 list-none flex-col gap-0.5 overflow-y-auto p-0',
          // Rows scrolled out of view fade at whichever edge they leave by.
          hasMoreAbove &&
            !hasMoreBelow &&
            '[mask-image:linear-gradient(to_bottom,transparent,#000_2.5rem)]',
          hasMoreBelow &&
            !hasMoreAbove &&
            '[mask-image:linear-gradient(to_bottom,#000_calc(100%_-_2.5rem),transparent)]',
          hasMoreAbove &&
            hasMoreBelow &&
            '[mask-image:linear-gradient(to_bottom,transparent,#000_2.5rem,#000_calc(100%_-_2.5rem),transparent)]'
        )}
      >
        {pinnedConversations.length > 0 ? (
          <li className="px-2 pt-1 text-xs font-medium tracking-wide text-fg-secondary uppercase">
            Pinned
          </li>
        ) : null}
        {pinnedConversations.map(renderConversation)}
        {recentConversations.length > 0 && pinnedConversations.length > 0 ? (
          <li className="px-2 pt-3 text-xs font-medium tracking-wide text-fg-secondary uppercase">
            Recent
          </li>
        ) : null}
        {recentConversations.map(renderConversation)}
      </ul>

      {visibleHasMore ? (
        <Button
          size="sm"
          variant="link"
          className="justify-start"
          disabled={visibleIsLoadingMore}
          onClick={() => void (isSearching ? search.loadMore() : loadMore())}
        >
          {visibleIsLoadingMore ? 'Loading…' : 'Load More'}
        </Button>
      ) : null}

      <Dialog
        open={renameConversation !== undefined}
        onOpenChange={(open) => {
          if (!open) setRenameConversationId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Chat</DialogTitle>
            <DialogDescription>
              Give this conversation a title you can find later.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitRename();
            }}
          >
            <Input
              autoFocus
              maxLength={80}
              aria-label="Chat title"
              value={renameTitle}
              onChange={(event) => setRenameTitle(event.target.value)}
            />
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameConversationId(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!renameTitle.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatConversationList;
